import React, { useState, useEffect, useRef, useCallback } from "react";
import { useLocation } from "react-router-dom";
import PictureImage from '../Common/PictureImage';
import {
  LOGO_IMAGE_HEIGHT,
  LOGO_IMAGE_WEBP,
  LOGO_IMAGE_WIDTH,
} from '../../data/optimizedHomepageAssets';
import { PrefetchLink } from "../../routing/PrefetchLink";
import { BUSINESS_PHONE_DISPLAY, BUSINESS_PHONE_TEL } from "../../seo/businessContact";
import "./Header.css";

/** Amazon FBA Nested Links */
const AMAZON_FBA_SERVICES_LINKS: readonly { to: string; label: string }[] = [
  // { to: "/amazon-fba-prep-services", label: "FBA prep services" },
  { to: "/amazon-fba-prep-centre-services", label: "Prep centre services" },
  { to: "/amazon-fba-shipments", label: "Amazon FBA shipments" },
  { to: "/amazon-fba-prep-pricing", label: "FBA prep pricing" },
  { to: "/amazon-fba-prep-private-label", label: "Private label FBA prep" },
  { to: "/amazon-fba-prep-wholesale", label: "Wholesale FBA prep" },
  { to: "/book-a-free-call", label: "Book a free call" },
];

/** Logistics Nested Links */
const LOGISTICS_SERVICES_LINKS: readonly { to: string; label: string }[] = [
  { to: "/ecommerce-fulfillment-services", label: "Ecommerce fulfilment services" },
  { to: "/b2b-fulfillment", label: "B2B fulfilment" },
  { to: "/warehousing-services", label: "Warehousing services" },
  { to: "/freight-forwarder-uk", label: "Freight Forwarder in UK" },
  { to: "/contract-packing", label: "Contract Packing" },
];

/** Fulfilment Nested Links */
const FULFILMENT_SERVICES_LINKS: readonly { to: string; label: string }[] = [
  { to: "/wms-integrations", label: "WMS Integrations" },
  { to: "/our-fulfilment-center", label: "our-fulfilment-center" },
];

const Header: React.FC = () => {
  const location = useLocation();
  const [servicesOpen, setServicesOpen] = useState(false);
  const [resourcesOpen, setResourcesOpen] = useState(false);
  const [mobileNestedOpen, setMobileNestedOpen] = useState<'amazon' | 'logistics' | 'fulfillment' | null>(null);
  const [mobileOpen, setMobileOpen] = useState(false);

  const servicesDropdownRef = useRef<HTMLDivElement>(null);
  const resourcesDropdownRef = useRef<HTMLDivElement>(null);
  const shellRef = useRef<HTMLDivElement>(null);

  const closeAllMenus = useCallback(() => {
    setMobileOpen(false);
    setServicesOpen(false);
    setResourcesOpen(false);
    setMobileNestedOpen(null);
    servicesDropdownRef.current?.querySelector<HTMLElement>('.dropdown-toggle')?.blur();
    resourcesDropdownRef.current?.querySelector<HTMLElement>('.dropdown-toggle')?.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  }, []);

  useEffect(() => {
    closeAllMenus();
  }, [location.pathname, closeAllMenus]);

  const toggleMobileNested = (key: 'amazon' | 'logistics' | 'fulfillment') => {
    if (!window.matchMedia('(max-width: 1000px)').matches) return;
    setMobileNestedOpen((current) => (current === key ? null : key));
  };

  const toggleServicesMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const willClose = servicesOpen;
    setServicesOpen((open) => {
      if (open) setMobileNestedOpen(null);
      return !open;
    });
    setResourcesOpen(false);
    if (willClose) event.currentTarget.blur();
  };

  const toggleResourcesMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
    event.stopPropagation();
    const willClose = resourcesOpen;
    setResourcesOpen((open) => !open);
    setServicesOpen(false);
    if (willClose) event.currentTarget.blur();
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      if (servicesDropdownRef.current && !servicesDropdownRef.current.contains(target)) {
        setServicesOpen(false);
      }
      if (resourcesDropdownRef.current && !resourcesDropdownRef.current.contains(target)) {
        setResourcesOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useEffect(() => {
    if (!mobileOpen) return;

    const scrollY = window.scrollY;
    document.body.classList.add('nav-open');
    document.body.style.top = `-${scrollY}px`;
    document.documentElement.classList.add('nav-open');

    return () => {
      document.body.classList.remove('nav-open');
      document.body.style.top = '';
      document.documentElement.classList.remove('nav-open');
      window.scrollTo(0, scrollY);
    };
  }, [mobileOpen]);

  useEffect(() => {
    const syncMobileNavOffset = () => {
      if (!shellRef.current) return;
      const shellHeight = shellRef.current.offsetHeight;
      document.documentElement.style.setProperty('--mobile-nav-top', `${shellHeight}px`);
    };

    syncMobileNavOffset();
    window.addEventListener('resize', syncMobileNavOffset);
    return () => window.removeEventListener('resize', syncMobileNavOffset);
  }, []);

  return (
    <div className="site-header-shell" ref={shellRef}>
      <div className="top-bar">
        <div className="container top-bar-inner">
          <div className="top-bar-item">
            <span className="top-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#FF6B35">
                <path d="M2 4h20v16H2V4zm10 7L3 6v12h18V6l-9 5z"/>
              </svg>
            </span>
            <span>info@pickpackpro.co.uk</span>
          </div>
          <div className="top-bar-item">
            <span className="top-icon">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="#FF6B35">
                <path d="M6.6 10.8c1.5 3 3.9 5.4 6.9 6.9l2.3-2.3c.3-.3.7-.4 1-.3 1.1.4 2.3.6 3.5.6.6 0 1 .4 1 1V21c0 .6-.4 1-1 1C10.7 22 2 13.3 2 3c0-.6.4-1 1-1h3.5c.6 0 1 .4 1 1 0 1.2.2 2.4.6 3.5.1.3 0 .7-.3 1l-2.2 2.3z"/>
              </svg>
            </span>
            <a href={BUSINESS_PHONE_TEL} className="phone-link">
              {BUSINESS_PHONE_DISPLAY}
            </a>
          </div>
        </div>
      </div>

      <header className="site-header">
        <div className="container header-inner">
          <PrefetchLink to="/" className="header-logo" onClick={closeAllMenus}>
            <PictureImage
              src={LOGO_IMAGE_WEBP}
              alt="Pick Pack Pro Logo"
              className="logo-image"
              width={LOGO_IMAGE_WIDTH}
              height={LOGO_IMAGE_HEIGHT}
              sizes="(max-width: 768px) 200px, 300px"
              isHero
            />
          </PrefetchLink>

          <div className="menu-toggle" onClick={() => setMobileOpen(!mobileOpen)}>
            <span className={`hamburger ${mobileOpen ? "open" : ""}`}></span>
          </div>

          {mobileOpen && (
            <button
              type="button"
              className="mobile-nav-backdrop"
              aria-label="Close menu"
              onClick={closeAllMenus}
            />
          )}

          <nav className={`header-nav ${mobileOpen ? "open" : ""}`}>
            <PrefetchLink to="/amazon-fba-prep-services" className="nav-link" onClick={closeAllMenus}>Amazon FBA</PrefetchLink>
            <PrefetchLink to="/shopify-fulfillment-services" className="nav-link" onClick={closeAllMenus}>Shopify Fulfilment</PrefetchLink>
            <PrefetchLink to="/tiktok-live-sales-fulfillment" className="nav-link tiktok-live-link" onClick={closeAllMenus}>
              TikTok Live <span className="live-dot"></span>
            </PrefetchLink>

            {/* SERVICES DROPDOWN — links always in DOM; opens on click */}
            <div className={`nav-dropdown ${servicesOpen ? 'is-open' : ''}`} ref={servicesDropdownRef}>
              <button
                type="button"
                className="nav-link dropdown-toggle"
                aria-expanded={servicesOpen}
                aria-haspopup="true"
                aria-controls="services-dropdown-menu"
                onClick={toggleServicesMenu}
              >
                Services
                <span className={`dropdown-arrow ${servicesOpen ? 'open' : ''}`}></span>
              </button>

              <div
                id="services-dropdown-menu"
                className={`dropdown-menu ${servicesOpen ? 'show' : ''}`}
              >
                <div className={`nested-flyout-container ${mobileNestedOpen === 'amazon' ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="dropdown-item-nested-trigger"
                    aria-expanded={mobileNestedOpen === 'amazon'}
                    onClick={(e) => { e.stopPropagation(); toggleMobileNested('amazon'); }}
                  >
                    Amazon FBA
                    <span className="dropdown-arrow-side">▶</span>
                  </button>
                  <div className="nested-box">
                    {AMAZON_FBA_SERVICES_LINKS.map(link => (
                      <PrefetchLink key={link.to} to={link.to} onClick={closeAllMenus} className="nested-link">{link.label}</PrefetchLink>
                    ))}
                  </div>
                </div>

                <div className={`nested-flyout-container ${mobileNestedOpen === 'logistics' ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="dropdown-item-nested-trigger"
                    aria-expanded={mobileNestedOpen === 'logistics'}
                    onClick={(e) => { e.stopPropagation(); toggleMobileNested('logistics'); }}
                  >
                    Logistics
                    <span className="dropdown-arrow-side">▶</span>
                  </button>
                  <div className="nested-box">
                    {LOGISTICS_SERVICES_LINKS.map(link => (
                      <PrefetchLink key={link.to} to={link.to} onClick={closeAllMenus} className="nested-link">{link.label}</PrefetchLink>
                    ))}
                  </div>
                </div>

                <div className={`nested-flyout-container ${mobileNestedOpen === 'fulfillment' ? 'is-open' : ''}`}>
                  <button
                    type="button"
                    className="dropdown-item-nested-trigger"
                    aria-expanded={mobileNestedOpen === 'fulfillment'}
                    onClick={(e) => { e.stopPropagation(); toggleMobileNested('fulfillment'); }}
                  >
                    Fulfilment
                    <span className="dropdown-arrow-side">▶</span>
                  </button>
                  <div className="nested-box">
                    {FULFILMENT_SERVICES_LINKS.map(link => (
                      <PrefetchLink key={link.to} to={link.to} onClick={closeAllMenus} className="nested-link">{link.label}</PrefetchLink>
                    ))}
                  </div>
                </div>

                <PrefetchLink to="/tiktok-shop-fulfillment-services" onClick={closeAllMenus}>TikTok Fulfilment</PrefetchLink>
                <PrefetchLink to="/multi-channel-fulfillment-services" onClick={closeAllMenus}>Multi Channel Fulfilment</PrefetchLink>
                <PrefetchLink to="/seller-fulfilled-prime-services" onClick={closeAllMenus}>Seller Fulfilled Prime</PrefetchLink>
                <PrefetchLink to="/fulfillment-by-merchant-services" onClick={closeAllMenus}>Fulfilment by Merchant</PrefetchLink>
                <PrefetchLink to="/return-handling-services" onClick={closeAllMenus}>Return Handling</PrefetchLink>
                <PrefetchLink to="/logistics-services" onClick={closeAllMenus}>Logistics Services</PrefetchLink>
              </div>
            </div>

            <div className={`nav-dropdown ${resourcesOpen ? 'is-open' : ''}`} ref={resourcesDropdownRef}>
              <button
                type="button"
                className="nav-link dropdown-toggle"
                aria-expanded={resourcesOpen}
                aria-haspopup="true"
                aria-controls="resources-dropdown-menu"
                onClick={toggleResourcesMenu}
              >
                Resources
                <span className={`dropdown-arrow ${resourcesOpen ? 'open' : ''}`}></span>
              </button>
              <div
                id="resources-dropdown-menu"
                className={`dropdown-menu ${resourcesOpen ? 'show' : ''}`}
              >
                <PrefetchLink to="/about-our-fulfillment-company" onClick={closeAllMenus}>About Us</PrefetchLink>
                  <PrefetchLink to="/careers" onClick={closeAllMenus}>Careers</PrefetchLink>
                <PrefetchLink to="/fulfillment-frequently-asked-questions" onClick={closeAllMenus}>FAQs</PrefetchLink>
                <PrefetchLink to="/fulfillment-blog" onClick={closeAllMenus}>Blogs</PrefetchLink>
                <PrefetchLink to="/pricing-fulfilment-services" onClick={closeAllMenus}>Pricing</PrefetchLink>
                <PrefetchLink to="/our-reviews" onClick={closeAllMenus}>Our Reviews</PrefetchLink>
                <PrefetchLink to="/integrations" onClick={closeAllMenus}>Integrations</PrefetchLink>
              </div>
            </div>

            <PrefetchLink to="/contact-fulfillment-services" className="btn-primary mobile-btn" onClick={closeAllMenus}>Contact Us</PrefetchLink>
            <PrefetchLink to="/logistics-services" className="btn-logistics-solid mobile-logistics-btn" onClick={closeAllMenus}>Logistics</PrefetchLink>
          </nav>

          <div className="header-cta-group">
            <PrefetchLink to="/contact-fulfillment-services" className="btn-primary btn-contact-solid desktop-btn" onClick={closeAllMenus}>
              Contact Us
            </PrefetchLink>
            <PrefetchLink to="/logistics-services" className="btn-logistics-solid" onClick={closeAllMenus}>
              Logistics
            </PrefetchLink>
          </div>
        </div>
      </header>
    </div>
  );
};

export default Header;
